import React, { useState, useEffect, useRef } from "react";
import { IHierarchicalNode} from "../../../utils/interfaces";
import { FaAngleDown } from "react-icons/fa";
import { IconWrapper } from "../../../customHooks/useIcon";
import { useUtils } from "../../../customHooks/useUtils";



// Type for flattened options in the dropdown
interface FlattenedOption {
  id: string;
  name: string;
  path: string;
  level: number;
  hasChildren?: boolean;
  isDevice?: boolean;
  deviceType?: string;
}

interface HierarchicalSelectorProps {
    data: IHierarchicalNode[];
    disableSelect?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (id: string) => void;
  selectedId?: string;
  className?: string;
  modalStyle?: string;
  showAll?: boolean;
}

export default function HierarchicalSelector({
  data,
  onSelect,
  selectedId = "",
  className = "",
    modalStyle = "",
  disableSelect = false,
  showAll = false,
}: HierarchicalSelectorProps): React.ReactElement {
  const {t} = useUtils();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [displayText, setDisplayText] = useState<string>("Select an option");
  const [flattenedOptions, setFlattenedOptions] = useState<FlattenedOption[]>(
    []
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    // Add the event listener when dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Flatten the hierarchical data for internal tracking - include all nodes but not devices
  useEffect(() => {
    const flattened: FlattenedOption[] = [];

    function flattenData(items: IHierarchicalNode[], path: string = ""): void {
      items.forEach((item) => {
        // Add all nodes (but not devices)
        const currentPath = path ? `${path} > ${item.name}` : item.name;
        const hasChildNodes = item.children && item.children.length > 0;

        flattened.push({
          id: item.id,
          name: item.name,
          path: currentPath,
          level: path.split(">").length,
          hasChildren: hasChildNodes,
        });

        // Recursively add children if they exist
        if (hasChildNodes) {
          flattenData(item.children, currentPath);
        }
      });
    }

    if (data && data.length > 0) {
      flattenData(data);
      setFlattenedOptions(flattened);
    }
  }, [data]);

  // Update display text when selection changes
  useEffect(() => {
    if (!selectedId && showAll) {
      setDisplayText(t("common.all"));
      return;
    }

    const selectedOption = flattenedOptions.find(
      (opt) => opt.id === selectedId
    );
    if (selectedOption) {
      setDisplayText(selectedOption.name);
    }
  }, [selectedId, flattenedOptions, showAll, t]);

  const handleSelect = (id: string, name: string): void => {
    onSelect(id);
    setDisplayText(name);
    setIsOpen(false);
  };
  return (
    <div className="" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disableSelect}
        type="button"
        className={`flex items-center justify-between px-4 py-2 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 ${className} disabled:cursor-not-allowed`}
      >
        <span className="mr-2">{displayText}</span>
        <IconWrapper
          icon={FaAngleDown}
          size={16}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50  w-64 max-h-80 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 ${modalStyle}`}
        >
          {showAll && (
            <div
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium"
              onClick={() => handleSelect("", "All")}
            >
              {t("common.all")}
            </div>
          )}

          {flattenedOptions.map((option) => (
            <div
              key={option.id}
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer font-medium"
              style={{ paddingLeft: `${option.level * 12 + 16}px` }}
              onClick={() => handleSelect(option.id, option.name)}
            >
              <div className="flex items-center">
                <span className="truncate">{option.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
